import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Table2, LineChart as LineChartIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import i18n from "../../i18n";

import "./finops-charts.css";

/* ---------------------------------------------------------------------------
 * Formatting
 * ------------------------------------------------------------------------ */

/**
 * Whole units as shown. Sums of converted amounts leave a fraction of a unit (e.g. -0.2 UZS when
 * salaries + expenses equal income), and Math.round(-0.2) is -0, which prints as "-0": `|| 0`
 * turns -0 into 0. Use it for the sign/colour too, so a shown 0 is never red.
 */
export const wholeMoney = (value) => {
  const n = Number(value) || 0;
  return (Math.sign(n) * Math.round(Math.abs(n))) || 0; // .5 away from zero, as toLocaleString did
};

/** "positive" | "negative" tone of an amount, judged on what is displayed. */
export const signTone = (value) => (wholeMoney(value) >= 0 ? "positive" : "negative");

/** One-decimal percent without a "-0.0". */
export const fmtPct = (p) => {
  const s = Number(p).toFixed(1);
  return s === "-0.0" ? "0.0" : s;
};

export const fmtMoney = (value, currency) =>
  currency === "UZS"
    ? `${wholeMoney(value).toLocaleString()} UZS`
    : `$${wholeMoney(value).toLocaleString()}`;

/** One decimal, but only when it carries information: 3000 -> "3", 2250 -> "2.3". */
const short = (n) => n.toFixed(1).replace(/\.0$/, "");

/**
 * Compact axis ticks — full precision lives in the tooltip and table.
 *
 * Keeps one decimal so neighbouring ticks stay distinguishable: rounding to
 * whole units made 2250 and 1500 both render as "$2k", producing two
 * identical labels on the same axis.
 */
export const fmtAxis = (value, currency) => {
  const v = Number(value || 0);
  const abs = Math.abs(v);
  const sign = v < 0 && Math.round(abs) > 0 ? "-" : ""; // no "-0" for a tiny negative tick
  if (currency === "UZS") {
    if (abs >= 1e9) return `${sign}${short(abs / 1e9)}B`;
    if (abs >= 1e6) return `${sign}${short(abs / 1e6)}M`;
    if (abs >= 1e3) return `${sign}${short(abs / 1e3)}K`;
    return `${sign}${Math.round(abs)}`;
  }
  if (abs >= 1e6) return `${sign}$${short(abs / 1e6)}M`;
  if (abs >= 1e3) return `${sign}$${short(abs / 1e3)}k`;
  return `${sign}$${Math.round(abs)}`;
};

/**
 * Trailing months with no activity at all are "hasn't happened yet", not
 * "revenue went to zero" — plotting them draws a cliff that isn't in the data.
 * Interior zero months are genuine and stay.
 */
export function trimTrailingEmpty(rows, keys) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  let last = -1;
  rows.forEach((row, i) => {
    if (keys.some((k) => Math.abs(Number(row?.[k]) || 0) > 0.005)) last = i;
  });
  return last === -1 ? [] : rows.slice(0, last + 1);
}

/* ---------------------------------------------------------------------------
 * Shared chrome
 * ------------------------------------------------------------------------ */

const AXIS_TICK = { fontSize: 11, fill: "var(--viz-axis)" };

/** Values in text ink with a color swatch carrying identity — never colored text. */
function VizTooltip({ active, payload, label, currency, totalLabel }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value !== undefined && p.value !== null);
  const total = rows.reduce((sum, p) => sum + (Number(p.value) || 0), 0);

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 shadow-lg">
      <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 mb-1.5">
        {label}
      </div>
      <div className="space-y-1">
        {rows.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2 text-xs">
            <span
              className="h-2 w-2 rounded-[2px] shrink-0"
              style={{ background: p.color || p.fill }}
              aria-hidden="true"
            />
            <span className="text-slate-500 dark:text-slate-400">{p.name}</span>
            <span className="ml-auto pl-3 font-medium text-slate-900 dark:text-slate-100 tabular-nums">
              {fmtMoney(p.value, currency)}
            </span>
          </div>
        ))}
      </div>
      {totalLabel && rows.length > 1 && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-100 dark:border-slate-700 flex items-center gap-2 text-xs">
          <span className="text-slate-500 dark:text-slate-400">{totalLabel}</span>
          <span className="ml-auto pl-3 font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
            {fmtMoney(total, currency)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Card shell that can flip between the plot and its table twin. The table is
 * the WCAG-clean equivalent every chart here needs (and the relief the
 * light-mode aqua contrast warning requires).
 */
export function ChartCard({ title, subtitle, action, children, table }) {
  const { t } = useTranslation("charts");
  const [showTable, setShowTable] = useState(false);
  return (
    <div className="finops-viz bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {title}
          </h3>
          {subtitle && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {action}
          {table && (
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              title={showTable ? t("toggle.showChart") : t("toggle.showTable")}
              aria-label={showTable ? t("toggle.showChart") : t("toggle.showTable")}
              className="rounded-md p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              {showTable ? (
                <LineChartIcon className="h-4 w-4" />
              ) : (
                <Table2 className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>
      {showTable && table ? table : children}
    </div>
  );
}

/** Legend swatches — always present for ≥2 series so identity is never color-alone. */
export function VizLegend({ items }) {
  const { t } = useTranslation("charts");
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
      {items.map((it) => (
        <span key={it.labelKey || it.label} className="inline-flex items-center gap-1.5 text-xs">
          <span
            className="h-2.5 w-2.5 rounded-[3px]"
            style={{ background: it.color }}
            aria-hidden="true"
          />
          <span className="text-slate-600 dark:text-slate-400">{it.labelKey ? t(it.labelKey) : it.label}</span>
        </span>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Distribution strip — where this period's income went, at a glance
 * ------------------------------------------------------------------------ */

export function DistributionBar({ salary, operational, retained, currency }) {
  const { t } = useTranslation("charts");
  const total = (salary || 0) + (operational || 0) + Math.max(retained || 0, 0);
  if (total <= 0) return null;

  const segments = [
    { key: "salary", label: t("series.salary"), value: salary || 0, color: "var(--viz-salary)" },
    { key: "operational", label: t("series.operational"), value: operational || 0, color: "var(--viz-operational)" },
    { key: "retained", label: t("series.retained"), value: Math.max(retained || 0, 0), color: "var(--viz-retained)" },
  ].filter((s) => s.value > 0);

  return (
    <div className="finops-viz">
      {/* 2px surface gaps between segments instead of borders */}
      <div className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full">
        {segments.map((s) => (
          <div
            key={s.key}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
            className="first:rounded-l-full last:rounded-r-full"
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5">
        {segments.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-xs">
            <span
              className="h-2.5 w-2.5 rounded-[3px]"
              style={{ background: s.color }}
              aria-hidden="true"
            />
            <span className="text-slate-600 dark:text-slate-400">{s.label}</span>
            <span className="font-medium text-slate-900 dark:text-slate-100 tabular-nums">
              {((s.value / total) * 100).toFixed(1)}%
            </span>
            <span className="text-slate-400 dark:text-slate-500 tabular-nums">
              {fmtMoney(s.value, currency)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Income trend — one series, so no legend; the title names it
 * ------------------------------------------------------------------------ */

export function IncomeTrendChart({ data, currency, height = 260 }) {
  const { t } = useTranslation("charts");
  const rows = data || [];
  if (rows.length === 0) {
    return <EmptyPlot height={height} />;
  }
  const peak = rows.reduce((m, r) => (r.value > (m?.value ?? -Infinity) ? r : m), rows[0]);

  return (
    <div className="finops-viz" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {/* Roomy top margin so the peak label clears the topmost gridline. */}
        <AreaChart data={rows} margin={{ top: 30, right: 20, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id="incomeFade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--viz-income)" stopOpacity={0.22} />
              <stop offset="100%" stopColor="var(--viz-income)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: "var(--viz-grid)" }}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={currency === "UZS" ? 52 : 46}
            tickFormatter={(v) => fmtAxis(v, currency)}
          />
          <Tooltip
            cursor={{ stroke: "var(--viz-axis)", strokeWidth: 1 }}
            content={<VizTooltip currency={currency} />}
          />
          <Area
            isAnimationActive={false}
            type="monotone"
            dataKey="value"
            name={t("series.income")}
            stroke="var(--viz-income)"
            strokeWidth={2}
            fill="url(#incomeFade)"
            dot={{ r: 3, strokeWidth: 0, fill: "var(--viz-income)" }}
            activeDot={{ r: 5, stroke: "var(--viz-surface)", strokeWidth: 2 }}
          >
            {/* Label the peak only — a number on every point is noise. Anchor
                it inward at the ends so it never sits over the y-axis ticks or
                runs off the right edge. */}
            <LabelList
              dataKey="value"
              content={({ x, y, value, index }) => {
                if (rows[index] !== peak) return null;
                const isFirst = index === 0;
                const isLast = index === rows.length - 1;
                return (
                  <text
                    x={isFirst ? x + 6 : isLast ? x - 6 : x}
                    y={y - 12}
                    textAnchor={isFirst ? "start" : isLast ? "end" : "middle"}
                    className="fill-slate-900 dark:fill-slate-100"
                    style={{ fontSize: 11, fontWeight: 600 }}
                  >
                    {fmtAxis(value, currency)}
                  </text>
                );
              }}
            />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Composition — salary + operational + retained stack back up to income
 * ------------------------------------------------------------------------ */

export function CompositionChart({ data, currency, height = 280 }) {
  const { t } = useTranslation("charts");
  const rows = data || [];
  if (rows.length === 0) return <EmptyPlot height={height} />;
  const hasLoss = rows.some((r) => (r.retained || 0) < 0);

  return (
    <div className="finops-viz" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 16, left: 4, bottom: 4 }} barCategoryGap="22%" maxBarSize={84}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: "var(--viz-grid)" }}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={currency === "UZS" ? 52 : 46}
            tickFormatter={(v) => fmtAxis(v, currency)}
          />
          {hasLoss && <ReferenceLine y={0} stroke="var(--viz-axis)" strokeWidth={1} />}
          <Tooltip
            cursor={{ fill: "var(--viz-grid)", fillOpacity: 0.35 }}
            content={<VizTooltip currency={currency} totalLabel={t("series.income")} />}
          />
          {/* 2px surface strokes act as the gap between stacked segments */}
          <Bar
            isAnimationActive={false}
            dataKey="salary"
            name={t("series.salary")}
            stackId="money"
            fill="var(--viz-salary)"
            stroke="var(--viz-surface)"
            strokeWidth={2}
          />
          <Bar
            isAnimationActive={false}
            dataKey="operational"
            name={t("series.operational")}
            stackId="money"
            fill="var(--viz-operational)"
            stroke="var(--viz-surface)"
            strokeWidth={2}
          />
          <Bar
            isAnimationActive={false}
            dataKey="retained"
            name={t("series.retained")}
            stackId="money"
            stroke="var(--viz-surface)"
            strokeWidth={2}
            radius={[4, 4, 0, 0]}
          >
            {rows.map((r, i) => (
              <Cell
                key={i}
                fill={(r.retained || 0) < 0 ? "var(--viz-negative)" : "var(--viz-retained)"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Translation keys (charts namespace); VizLegend resolves them at render time.
export const COMPOSITION_LEGEND = [
  { labelKey: "series.salary", color: "var(--viz-salary)" },
  { labelKey: "series.operationalExpense", color: "var(--viz-operational)" },
  { labelKey: "series.retained", color: "var(--viz-retained)" },
];

/* ---------------------------------------------------------------------------
 * Entity colors
 * ------------------------------------------------------------------------ */

export const SERIES_SLOTS = [
  "var(--viz-s1)", "var(--viz-s2)", "var(--viz-s3)", "var(--viz-s4)",
  "var(--viz-s5)", "var(--viz-s6)", "var(--viz-s7)", "var(--viz-s8)",
];
export const OTHER_COLOR = "var(--viz-other)";

/**
 * Stable color for an entity, keyed off its id rather than its row position —
 * so re-ranking or filtering never repaints the entities that remain. Two
 * entities can land on the same hue; that's acceptable here because every chart
 * using this labels its marks directly, so hue is reinforcement, not identity.
 */
export function colorForKey(key) {
  const s = String(key ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 100000;
  return SERIES_SLOTS[h % SERIES_SLOTS.length];
}

/**
 * Keep a part-to-whole chart inside the ~6 slices a ring can carry: the top
 * `max - 1` entries stay, the tail folds into one neutral "Boshqalar" slice.
 */
export function foldTail(rows, max = 6, otherLabel = i18n.t("charts:other")) {
  const sorted = [...(rows || [])].sort((a, b) => b.value - a.value);
  if (sorted.length <= max) return sorted;
  const head = sorted.slice(0, max - 1);
  const tail = sorted.slice(max - 1);
  return [
    ...head,
    {
      name: otherLabel,
      value: tail.reduce((s, r) => s + (r.value || 0), 0),
      isOther: true,
      count: tail.length,
    },
  ];
}

/* ---------------------------------------------------------------------------
 * Donut — part-to-whole at a glance, with the total in the hole
 * ------------------------------------------------------------------------ */

export function DonutChart({ data, currency, height = 260, centerLabel }) {
  const { t } = useTranslation("charts");
  const rows = foldTail(data, 6, t("other"));
  const total = rows.reduce((s, r) => s + (r.value || 0), 0);
  if (rows.length === 0 || total <= 0) return <EmptyPlot height={height} />;

  const withColor = rows.map((r, i) => ({
    ...r,
    color: r.isOther ? OTHER_COLOR : SERIES_SLOTS[i % SERIES_SLOTS.length],
    pct: (r.value / total) * 100,
  }));

  return (
    <div className="finops-viz">
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <div className="relative shrink-0" style={{ width: height, height }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={withColor}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="88%"
                startAngle={90}
                endAngle={-270}
                isAnimationActive={false}
                /* 2px surface stroke is the gap between segments */
                stroke="var(--viz-surface)"
                strokeWidth={2}
              >
                {withColor.map((r) => (
                  <Cell key={r.name} fill={r.color} />
                ))}
              </Pie>
              <Tooltip content={<VizTooltip currency={currency} />} />
            </PieChart>
          </ResponsiveContainer>
          {/* Total in the hole — information a plain pie can't carry */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {centerLabel ?? t("total")}
            </span>
            <span className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {fmtAxis(total, currency)}
            </span>
          </div>
        </div>

        {/* Legend carries name + value + share, so no labels collide on the ring */}
        <ul className="flex-1 min-w-0 w-full space-y-1.5">
          {withColor.map((r) => (
            <li key={r.name} className="flex items-center gap-2 text-xs">
              <span
                className="h-2.5 w-2.5 rounded-[3px] shrink-0"
                style={{ background: r.color }}
                aria-hidden="true"
              />
              <span className="text-slate-600 dark:text-slate-400 truncate capitalize">
                {r.name}
                {r.isOther && r.count ? ` (${r.count})` : ""}
              </span>
              <span className="ml-auto pl-2 font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                {fmtMoney(r.value, currency)}
              </span>
              <span className="w-11 text-right text-slate-400 dark:text-slate-500 tabular-nums">
                {r.pct.toFixed(1)}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Single-series time bars — discrete monthly amounts
 * ------------------------------------------------------------------------ */

export function TrendBars({ data, currency, name, color = "var(--viz-operational)", height = 260 }) {
  const { t } = useTranslation("charts");
  const rows = data || [];
  if (rows.length === 0) return <EmptyPlot height={height} />;

  return (
    <div className="finops-viz" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 16, left: 4, bottom: 4 }} barCategoryGap="26%" maxBarSize={84}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: "var(--viz-grid)" }}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={currency === "UZS" ? 52 : 46}
            tickFormatter={(v) => fmtAxis(v, currency)}
          />
          <Tooltip
            cursor={{ fill: "var(--viz-grid)", fillOpacity: 0.35 }}
            content={<VizTooltip currency={currency} />}
          />
          <Bar isAnimationActive={false} dataKey="value" name={name ?? t("series.amount")} fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Ranked horizontal bars — one series, one color (never a value-ramp)
 * ------------------------------------------------------------------------ */

/**
 * `colorBy` opts each bar into its own entity color (keyed off `row.colorKey`,
 * falling back to the name) instead of one hue for the whole series. Identity
 * still comes from the axis label and the value from the end label, so the hue
 * is reinforcement — and because it keys off the entity, re-ranking between
 * periods doesn't repaint anyone.
 */
export function RankedBars({ data, currency, color = "var(--viz-salary)", colorBy = false, height = 300 }) {
  const { t } = useTranslation("charts");
  const rows = data || [];
  if (rows.length === 0) return <EmptyPlot height={height} />;

  return (
    <div className="finops-viz" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 4, right: 56, left: 4, bottom: 4 }}
          barCategoryGap="26%"
        >
          <CartesianGrid horizontal={false} stroke="var(--viz-grid)" />
          <XAxis
            type="number"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => fmtAxis(v, currency)}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ ...AXIS_TICK, fill: "var(--viz-text-secondary)" }}
            tickLine={false}
            axisLine={false}
            width={148}
          />
          <Tooltip
            cursor={{ fill: "var(--viz-grid)", fillOpacity: 0.35 }}
            content={<VizTooltip currency={currency} />}
          />
          <Bar isAnimationActive={false} dataKey="value" name={t("series.amount")} fill={color} radius={[0, 4, 4, 0]} barSize={16}>
            {colorBy &&
              rows.map((r, i) => (
                <Cell key={i} fill={colorForKey(r.colorKey ?? r.name)} />
              ))}
            <LabelList
              dataKey="value"
              position="right"
              formatter={(v) => fmtAxis(v, currency)}
              className="fill-slate-600 dark:fill-slate-300"
              style={{ fontSize: 11 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Table twins
 * ------------------------------------------------------------------------ */

export function SimpleTable({ columns, rows }) {
  const { t } = useTranslation("charts");
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700">
            {columns.map((c) => (
              <th
                key={c.key}
                className={`py-2 px-2 font-medium text-slate-500 dark:text-slate-400 ${
                  c.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`py-1.5 px-2 ${
                    c.align === "right"
                      ? "text-right tabular-nums text-slate-900 dark:text-slate-100"
                      : "text-slate-600 dark:text-slate-400"
                  }`}
                >
                  {r[c.key]}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-center text-slate-400 dark:text-slate-500"
              >
                {t("common:status.noData")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function EmptyPlot({ height }) {
  const { t } = useTranslation("charts");
  return (
    <div
      className="flex items-center justify-center text-sm text-slate-400 dark:text-slate-500"
      style={{ height }}
    >
      {t("empty.noDataPeriod")}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Stat tile
 * ------------------------------------------------------------------------ */

export function StatTile({ label, value, sub, tone = "neutral", icon: Icon, emphasis = false }) {
  const toneClass =
    tone === "positive"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "negative"
      ? "text-red-600 dark:text-red-400"
      : "text-slate-900 dark:text-slate-100";

  return (
    <div
      className={`rounded-xl border p-4 transition-shadow hover:shadow-sm ${
        emphasis
          ? "border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/80"
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
      }`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />}
        <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <div className={`${emphasis ? "text-3xl" : "text-2xl"} font-semibold ${toneClass}`}>
        {value}
      </div>
      {sub && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate" title={sub}>
          {sub}
        </p>
      )}
    </div>
  );
}

/** Percentage-of-income helper used by both dashboards. */
export function pctOf(part, whole) {
  if (!whole) return null;
  return (part / whole) * 100;
}

export const useTrimmed = (rows, keys) =>
  useMemo(() => trimTrailingEmpty(rows, keys), [rows, keys]);
